import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { BaseLogo } from "../../core/models/entities/BaseLogo";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { actAsCacheKey } from "../../contexts/actAsStore";
import { PageQuery, toQueryParam } from "../../utils/models/pageQuery";
import { Organization } from "../../core/models/entities/Organization";
import { PagedResponse } from "../models/responses/pagedResponse";
import { mapToLogo } from "../models/mappers/mapLogo";
import { markLogoListAsStale } from "../../utils/functions/logo-refresh.util";
import { LogoOptions } from "../../core/models/entities/LogoOptions";
import { mapLogoOptions } from "../models/mappers/mapLogoOptions";
import { TransferOptions } from "../../core/models/entities/TransferOptions";
import { mapTransferOptions } from "../models/mappers/mapTransferOptions";
import {
  ChangeLogoColors,
  InitiateApproval,
} from "../../presentation/screens/logo-list/hooks/useLogoActionsHandler";
import { LogoCommit } from "../models/bodies/LogoCommit";
import { CreateMaskQueueCount } from "../models/responses/CreateMaskQueueCount";
import { Verdict } from "../../presentation/components/ApprovalVoting/approval-voting";
import { LogoStorageCountByOrgDto } from "../models/responses/LogoDto";
import { enviroment } from "../../enviroment";
import {
  cancelInFlightDetailFetch,
  isLogoDetailQueryKey,
} from "./logoDetailCache";
import { withClick } from "../clients/clientCorrelation";
import { CustomerLogoAttachResultDto } from "../models/responses/CustomerLogoAttachResultDto";
import { CustomerLogoLink } from "../../core/models/entities/CustomerLogoLink";
import { normalizeLinks } from "../models/mappers/mapCustomerLogoLinks";
import {
  isCloneUploadPendingError,
  withCloneUploadRetry,
  withProductPendingRetry,
} from "../clients/productPendingRetry";
import { inferApprovalClampAfterRetry } from "../../utils/functions/maxSizeClamp";
import { SupplierStatus } from "../../core/models/entities/SupplierStatus";
import {
  mapSupplierStatus,
  supplierStatusRouteMissing,
} from "../models/mappers/mapSupplierStatus";
import {
  isSupplierStatusRouteMissing,
  markSupplierStatusRouteMissing,
} from "../../utils/functions/supplierStatus";
import { helperCatalogOptions } from "./helperCache";
import { useEffect } from "react";

export interface LogoListOptions {
  keepPreviousData?: boolean;
  // Opt-out gate for the list query (default true = exactly the historical
  // query). The customer edit screen's Logos section uses it so a search
  // fires only for a debounced term of 2+ characters and the customer's own
  // list only on the first visit of the section.
  enabled?: boolean;
  // Overrides the list's 60 s staleTime (omitted = exactly the historical
  // query). The customer edit screen's Logos section passes the same value
  // its prefetch wrote the entry with (flag fast), so the entry is not
  // refetched on the click just because a minute passed.
  staleTime?: number;
}

// True for the LIST key of a page filtered on ONE customer org
// (useAllLogos with filterBy customer.id = customerId): ["logos", actAs,
// typeFilter, qs] - and the infinite variant ["logos", "inf", actAs, qs] of
// the same filtered list - where qs carries the contiguous filter pair
// toQueryParam emits. Lets the manual-link mutations refresh exactly the
// customer's own pages and leave every other cached logo list (and every
// detail entry) alone.
export function isCustomerLogoListQueryKey(
  key: readonly unknown[],
  customerId: number,
): boolean {
  if (key[0] !== "logos" || key.length !== 4 || typeof key[3] !== "string") {
    return false;
  }
  return new RegExp(
    `(?:^|[?&])filterByField=customer\\.id&filterByValue=${customerId}(?:&|$)`,
  ).test(key[3]);
}

export interface LogoRepository {
  changeColors(
    orgId: Organization["id"],
    logoId: BaseLogo["id"],
    colors: ChangeLogoColors,
  ): Promise<BaseLogo>;
  changeTitle(
    orgId: Organization["id"],
    logoId: BaseLogo["id"],
    title: string,
  ): Promise<BaseLogo>;
  useLogo(
    orgId: number,
    logoId: number,
    options?: LogoDetailSubscriptionOptions,
  ): Resource<BaseLogo>;
  useOptions(options?: { enabled?: boolean }): Resource<LogoOptions>;
  prefetchOptions(): Promise<void>;
  useTransferOptions(
    vendorId?: number,
    options?: { enabled?: boolean },
  ): Resource<TransferOptions>;
  prefetchTransferOptions(vendorId?: number): Promise<void>;
  useAllLogos(
    currentPageQuery?: PageQuery,
    options?: LogoListOptions,
  ): Resource<PagedResponse<BaseLogo>>;
  /**
   * The customer's logo links (flag REACT_APP_CUSTOMER_LOGOS_TAB_ENABLED),
   * normalised: the API's int[] answer (its sub-flag
   * CustomerLogoLinkSourceEnabled off) = the MANUALLY linked ids as 'manual'
   * removable links; its object answer (sub-flag on) = every customer link
   * with source / added-by / removable (flag
   * REACT_APP_CUSTOMER_LOGOS_TAB_V2_ENABLED shows them). GET
   * organizations/{orgId}/logos/attach, key ["logo-links", customerId]. The
   * API answers 404 while its flag CustomerLogoAttachEnabled is off
   * (surfaced as the query's error, never retried).
   */
  useCustomerLogoLinks(
    customerId: number,
    options?: { enabled?: boolean; staleTime?: number },
  ): Resource<CustomerLogoLink[]>;
  /**
   * Warm the links cache ahead of the section's first visit (flag
   * REACT_APP_CUSTOMER_LOGOS_TAB_FAST_ENABLED): the SAME key, queryFn and
   * retry policy as useCustomerLogoLinks, and the staleTime the caller
   * also gives the hook (default the hook's 60 s), so the hook reads the
   * prefetched entry (or joins the in-flight request) instead of firing a
   * second GET. Fail-silent: prefetchQuery never rejects, a miss only means
   * the hook fetches exactly as before. A FAILED entry (the flag-off 404)
   * is never fresh to react-query: the hook refetches it on its first
   * visit (1 GET + 1 GET), and a further prefetch on a cached error is
   * skipped (0 GET).
   */
  prefetchCustomerLogoLinks(
    customerId: number,
    options?: { staleTime?: number },
  ): Promise<void>;
  /**
   * Link the dealer's logos to the customer (1 POST, idempotent). Afterwards
   * ONLY the customer's own list pages (GET logos?filterByField=customer.id)
   * and the links query are invalidated - never the dealer's whole logo cache
   * or the detail entries.
   */
  attachLogosToCustomer(
    customerId: number,
    logoIds: number[],
  ): Promise<CustomerLogoAttachResultDto>;
  /**
   * Remove ONE link (1 DELETE); same narrow invalidation as attach. Rejects
   * with the API's DataError: 404 = not a removable link (sub-flag off) /
   * not linked, 409 { code: "notRemovable", reason } = the link's source
   * forbids it (sub-flag on).
   */
  detachLogoFromCustomer(customerId: number, logoId: number): Promise<void>;
  /**
   * Warm a list page's cache entry (same key as useAllLogos). staleTime
   * defaults to the list's 60 s; the customer logo tab's fast prefetch
   * passes the longer one it also gives its hook. Never retried (react-
   * query's fetchQuery defaults retry to false and the app's QueryClient
   * sets none): a failed prefetch is exactly 1 GET, the hook then fetches
   * as today. Never rejects.
   */
  prefetchLogos(
    query: PageQuery,
    options?: { staleTime?: number },
  ): Promise<void>;
  useAllLogosForDesignInfinity(
    customerId: number,
    currentPageQuery?: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>>;
  useAllLogosInfinity(
    query: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>>;
  createLogo(logo: File, orgId: number): Promise<BaseLogo>;
  createTextLogo(
    value: string,
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo>;
  createNameLogo(
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo>;
  createNumberLogo(
    color: string,
    heightMm: number,
    font: string,
    method: string,
    orgId: number,
    customerId?: number,
  ): Promise<BaseLogo>;
  createBaseLogo(customerId: number, title: string): Promise<BaseLogo>;
  uploadFileToBaseLogo(
    logo: File,
    customerId: number,
    logoId: number,
  ): Promise<BaseLogo>;
  createAndUploadBaseLogo(
    logo: File,
    customerId: number,
    title: string,
  ): Promise<BaseLogo>;
  update(orgId: number, logo: BaseLogo): Promise<BaseLogo>;
  resize(orgId: number, logo: BaseLogo): Promise<BaseLogo>;
  copy(orgId: number, logoId: number): Promise<BaseLogo>;
  commit(logo: BaseLogo, commit: LogoCommit): Promise<BaseLogo>;
  initiateApproval(
    logo: BaseLogo,
    approval: InitiateApproval,
  ): Promise<BaseLogo>;
  sendApproval(logo: BaseLogo, email: string): Promise<void>;
  sendApprovalConditional(logo: BaseLogo, email: string): Promise<void>;
  placeVerdict(logo: BaseLogo, verdict: Verdict): Promise<BaseLogo>;
  delete(orgId: number, logoId: number): Promise<void>;
  generateTextLogoPreview: (
    value: string,
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
  ) => Promise<string>;
  generateNumberLogoPreview: (
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
  ) => Promise<string>;
  refreshStorageCount(logoId: BaseLogo["id"]): Promise<void>;
  getLogoStorageCounts(
    logoId: BaseLogo["id"],
  ): Promise<LogoStorageCountByOrgDto[]>;
  refreshLogoStorageCountForOrg(
    logoId: BaseLogo["id"],
    organizationId: number,
  ): Promise<void>;
  getImportLogosQueueCounts(): Promise<CreateMaskQueueCount[]>;
  useImportLogosQueueCounts(options?: { open?: boolean }): Resource<CreateMaskQueueCount[]>;
  getImportPricesQueueCounts(): Promise<CreateMaskQueueCount[]>;
  useImportPricesQueueCounts(): Resource<CreateMaskQueueCount[]>;
  /**
   * The logo supplier's availability (GET supplier-status, flag
   * REACT_APP_SUPPLIER_STATUS_ENABLED): ONE read, mapped. A 404 from the
   * route (API flag SupplierStatusRouteEnabled off / not deployed) is not
   * an error: the answer is a silent "ok" with routeMissing=true and the
   * tab remembers it for SUPPLIER_STATUS_ROUTE_MISSING_TTL_MS (15 min: 0
   * further reads meanwhile, then one more when it matters). Every other failure
   * rejects with the DataError - the banner then stays hidden. The query
   * (key ["supplier-status"]) lives in useSupplierStatus, not here.
   */
  getSupplierStatus(): Promise<SupplierStatus>;
}

// The options catalogs (logo-options and transfer-options/GetTemplatesSizes)
// change rarely and are Redis-cached server-side. GetTemplatesSizes is also a
// heavy NAV call (p50 ~0.8s, p95 ~6s), so a long staleTime lets re-opening the
// Commission/Pricing sections (and idle/hover prefetches) hit the cache
// instead of showing a spinner.
const OPTIONS_CATALOG_STALE_TIME = 30 * 60 * 1000; // 30 min

// Detail query key form: ["logos", actAsSegment, logoId] — BOTH numbers.
// The detail-key predicate (["logos", actAs, id]) lives in the leaf module
// logoDetailCache (so the cache patches there can use it without an import
// cycle) and is re-exported here for its historical importers - still ONE
// definition.
export { isLogoDetailQueryKey } from "./logoDetailCache";
// Options for useLogo's second consumer (flag REACT_APP_LOGO_HEAL_PER_MUTATION):
// the editor view-models subscribe to the detail entry they already hold so
// a background heal reaches the open canvas. `enabled:false` = no observer
// at all; `staleTime` per observer (Infinity = never fetch on enable/mount,
// only on explicit invalidation). Omitted = exactly the historical query.
export interface LogoDetailSubscriptionOptions {
  enabled?: boolean;
  staleTime?: number;
}

export function useLogoRepository(): LogoRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  // Act-as-vendor: getAllLogos AND getLogo are scoped by ApiClient
  // (actAsOrgId param, #601/#603), so the impersonation scope must be part of
  // the list and detail cache keys — otherwise the admin's rows (or the
  // previous vendor's currency on a detail) are served from cache after
  // switching identity. 0 = not impersonating. See OrderRepository for the
  // full rationale. Same shared function as every out-of-repository detail
  // key writer (useCartSync, heals, clone seed, deep-link seed).
  const actAsKey = actAsCacheKey;
  async function invalidateQueries() {
    await queryClient.invalidateQueries({
      queryKey: ["logos"],
      type: "all",
    });
  }

  function useOptions(options?: { enabled?: boolean }): Resource<LogoOptions> {
    return useQuery(
      ["logo-options"],
      () => apiClient.getLogoOptions().then(mapLogoOptions),
      {
        staleTime: OPTIONS_CATALOG_STALE_TIME,
        enabled: options?.enabled ?? true,
        // Admin perf FE-4 (flag): 30 min cache + revalidate on entry.
        ...helperCatalogOptions(),
      },
    );
  }

  // Warm the logo-options cache (same key/queryFn/staleTime as useOptions) so
  // screens that gate on it are served from cache. Fail-silent: a miss just
  // means useOptions fetches as usual.
  function prefetchOptions(): Promise<void> {
    return queryClient.prefetchQuery(
      ["logo-options"],
      () => apiClient.getLogoOptions().then(mapLogoOptions),
      { staleTime: OPTIONS_CATALOG_STALE_TIME },
    );
  }

  // The key includes vendorId: the response is vendor-specific (NAV prices/
  // sizes per vendor), so with a long staleTime a shared key would serve one
  // vendor's catalog for another (e.g. an admin editing customers of two
  // different vendors).
  function useTransferOptions(
    vendorId?: number,
    options?: { enabled?: boolean },
  ): Resource<TransferOptions> {
    return useQuery(
      ["transfer-options", vendorId],
      () => apiClient.getTransferOptions(vendorId).then(mapTransferOptions),
      {
        staleTime: OPTIONS_CATALOG_STALE_TIME,
        enabled: options?.enabled ?? true,
      },
    );
  }

  // Warm the transfer-options cache (same key/queryFn/staleTime as
  // useTransferOptions) so the Commission & VAT / Pricing sections open
  // instantly instead of spinning on the heavy GetTemplatesSizes NAV call.
  // Fail-silent: prefetchQuery swallows errors, and a miss just means the
  // section's own lazy hook fetches as usual.
  function prefetchTransferOptions(vendorId?: number): Promise<void> {
    return queryClient.prefetchQuery(
      ["transfer-options", vendorId],
      () => apiClient.getTransferOptions(vendorId).then(mapTransferOptions),
      { staleTime: OPTIONS_CATALOG_STALE_TIME },
    );
  }

  function useLogo(
    orgId: number,
    logoId: number,
    options?: LogoDetailSubscriptionOptions,
  ): Resource<BaseLogo> {
    // actAsKey in the DETAIL key too: getLogo carries actAsOrgId since #603,
    // so the payload varies with the impersonation scope (prices in the
    // acted-as vendor's currency). Same slot position as the list keys; every
    // out-of-repository writer (useCartSync, clone/hi-res heals, deep-link
    // seed) MUST build the identical shape.
    return useQuery(
      ["logos", actAsKey(), logoId],
      () => apiClient.getLogo(orgId, logoId).then(mapToLogo),
      {
        // Short freshness window so a detail query mounted right after a
        // seedAndMarkStale (clone flow: copy seeds the cache, the editor mounts
        // <1s later) doesn't immediately re-fetch the response it was just
        // seeded with - each GetLogo costs ~1.4-1.8s server-side (SOAP+NAV) -
        // and doesn't overwrite the optimistic dims seed while the background
        // size restore is still in flight. Explicit invalidations (color
        // change, title, assign customer, ...) still refetch active queries
        // regardless of staleTime, so edit flows are unaffected.
        staleTime: options?.staleTime ?? 15_000,
        ...(options?.enabled === undefined ? {} : { enabled: options.enabled }),
      },
    );
  }

  function useAllLogos(
    query: PageQuery,
    options?: LogoListOptions,
  ): Resource<PagedResponse<BaseLogo>> {
    // If a logo type was filtered, extend the route by a type parameter.
    const typeFilter = query.filterBy.find((it) => it.field === "logoType");
    const queryCopy = { ...query };
    queryCopy.filterBy = queryCopy.filterBy.filter(
      (it) => it.field !== "logoType",
    );
    return useQuery(
      ["logos", actAsKey(), typeFilter?.value, toQueryParam(query)],
      ({ signal }) =>
        apiClient.getAllLogos(queryCopy, typeFilter?.value, signal).then(
          // Null-guard: a throw in this transform rejects the query and shows
          // the error view even on a 200 — degrade to an empty page instead.
          (data) => ({
            ...data,
            results: data.results ? data.results.map(mapToLogo) : [],
          }),
        ),
      {
        staleTime: options?.staleTime ?? 60000, // 1 min unless the caller says
        refetchOnWindowFocus: false,
        // keepPreviousData is OPT-IN (default off): with it, isLoading stays
        // false on query changes, which changes what consumers observe. The
        // list screens opt in and dim the previous rows via
        // isFetching/isPreviousData instead of collapsing into a spinner.
        // Other consumers (commission-editor's SKU lookup, admin import
        // screens, design editor) rely on isLoading gating and keep the
        // previous behavior. First load still shows the normal spinner.
        keepPreviousData: options?.keepPreviousData ?? false,
        // Omitted = react-query's default (enabled), so every existing caller
        // keeps the exact historical query.
        ...(options?.enabled === undefined ? {} : { enabled: options.enabled }),
      },
    );
  }

  // ----- Customer-logo links (flag REACT_APP_CUSTOMER_LOGOS_TAB_ENABLED) -----

  // The customer's links, normalised (mapCustomerLogoLinks): the API's old
  // int[] answer becomes 'manual' removable links - exactly what the section
  // treated the ids as before v2 - and the object answer (API sub-flag
  // CustomerLogoLinkSourceEnabled) keeps its source classification for the
  // v2 columns (flag REACT_APP_CUSTOMER_LOGOS_TAB_V2_ENABLED). Same query
  // key and the same single GET as before.
  function useCustomerLogoLinks(
    customerId: number,
    options?: { enabled?: boolean; staleTime?: number },
  ): Resource<CustomerLogoLink[]> {
    return useQuery(
      ["logo-links", customerId],
      async () =>
        normalizeLinks(await apiClient.getCustomerLogoAttachments(customerId)),
      {
        // 1 min - same as the logo list - unless the caller says otherwise
        // (the fast section passes the staleTime its prefetch wrote with).
        staleTime: options?.staleTime ?? 60000,
        refetchOnWindowFocus: false,
        // A 404 (API flag off) must cost exactly one GET, not four.
        retry: false,
        enabled: options?.enabled ?? true,
      },
    );
  }

  // Prefetch the links into the same cache useCustomerLogoLinks reads (flag
  // REACT_APP_CUSTOMER_LOGOS_TAB_FAST_ENABLED). Same key, same normalisation,
  // the caller's staleTime (default the hook's 60 s; a fresh entry is not
  // refetched) and the same retry: false. A FAILED entry is never fresh to
  // react-query (no dataUpdatedAt, retryOnMount default): the hook refetches
  // it the moment its query is enabled, so a flag-off 404 costs 1 GET here
  // + 1 GET on the first visit - and while such an error sits in the cache
  // (a revisit of the customer within the cache time) nothing is prefetched
  // at all: the section fetches on its first visit exactly as today.
  function prefetchCustomerLogoLinks(
    customerId: number,
    options?: { staleTime?: number },
  ): Promise<void> {
    const key = ["logo-links", customerId];
    if (queryClient.getQueryState(key)?.status === "error") {
      return Promise.resolve();
    }
    return queryClient.prefetchQuery(
      key,
      async () =>
        normalizeLinks(await apiClient.getCustomerLogoAttachments(customerId)),
      { staleTime: options?.staleTime ?? 60000, retry: false },
    );
  }

  // After a manual link changed: the links query and the customer's own list
  // pages. The dealer's logo list, the search results and every detail entry
  // are untouched (the search rows derive "Already added" from these two).
  async function invalidateCustomerLogoQueries(customerId: number) {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["logo-links", customerId] }),
      queryClient.invalidateQueries({
        queryKey: ["logos"],
        predicate: (query) =>
          isCustomerLogoListQueryKey(query.queryKey, customerId),
      }),
    ]);
  }

  async function attachLogosToCustomer(
    customerId: number,
    logoIds: number[],
  ): Promise<CustomerLogoAttachResultDto> {
    return withClick(async () => {
      const result = await apiClient.attachCustomerLogos(customerId, {
        logoIds,
      });
      await invalidateCustomerLogoQueries(customerId);
      return {
        accepted: result?.accepted ?? [],
        rejected: result?.rejected ?? [],
      };
    });
  }

  async function detachLogoFromCustomer(
    customerId: number,
    logoId: number,
  ): Promise<void> {
    return withClick(async () => {
      await apiClient.detachCustomerLogo(customerId, logoId);
      await invalidateCustomerLogoQueries(customerId);
    });
  }

  // Prefetch a page into the same cache useAllLogos reads. It only warms the
  // cache (never changes what the main query renders), and it respects the
  // same 60s staleTime so prices stay as fresh as a normal click - unless
  // the caller passes its own (the customer logo tab's fast prefetch pairs
  // 5 min with the same value on its hook). No retry: react-query's
  // fetchQuery defaults retry to false when none is set and the app's
  // QueryClient sets none, so a failed prefetch is exactly 1 GET.
  // Returns the (never-rejecting) prefetch promise so callers can track in-flight work.
  function prefetchLogos(
    query: PageQuery,
    options?: { staleTime?: number },
  ): Promise<void> {
    const typeFilter = query.filterBy.find((it) => it.field === "logoType");
    const queryCopy = { ...query };
    queryCopy.filterBy = queryCopy.filterBy.filter(
      (it) => it.field !== "logoType",
    );
    return queryClient.prefetchQuery(
      ["logos", actAsKey(), typeFilter?.value, toQueryParam(query)],
      () =>
        apiClient.getAllLogos(queryCopy, typeFilter?.value).then((data) => ({
          ...data,
          results: data.results ? data.results.map(mapToLogo) : [],
        })),
      { staleTime: options?.staleTime ?? 60000 },
    );
  }

  function useAllLogosForDesignInfinity(
    customerId: number,
    query: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>> {
    return useInfiniteQuery({
      queryKey: ["logos", customerId, "inf", toQueryParam(query)],
      queryFn: ({ pageParam }) => {
        const copy: PageQuery = {
          page: {
            index: pageParam ?? query.page.index,
            size: query.page.size,
          },
          filterBy: query.filterBy,
          sortBy: {
            field: query.sortBy.field,
            sortDirection: query.sortBy.sortDirection,
          },
          searchTerm: query.searchTerm,
        };

        return apiClient.getAllLogosForDesign(customerId, copy);
      },
      // Null-guards: a degraded page must not throw here — that puts the
      // editor's logo picker in an error state on a 200 response.
      getNextPageParam: (lastPage) => {
        if (!lastPage || lastPage.pageIndex === lastPage.pageCount) {
          return undefined;
        }
        return lastPage.pageIndex + 1;
      },
      getPreviousPageParam: (firstPage) => {
        if (!firstPage || firstPage.pageIndex === 1) {
          return undefined;
        }

        return firstPage.pageIndex - 1;
      },
    });
  }

  function useAllLogosInfinity(
    query: PageQuery,
  ): UseInfiniteQueryResult<PagedResponse<BaseLogo>> {
    return useInfiniteQuery({
      queryKey: ["logos", "inf", actAsKey(), toQueryParam(query)],
      queryFn: ({ pageParam }) => {
        const copy: PageQuery = {
          page: {
            index: pageParam ?? query.page.index,
            size: query.page.size,
          },
          filterBy: query.filterBy,
          sortBy: {
            field: query.sortBy.field,
            sortDirection: query.sortBy.sortDirection,
          },
          searchTerm: query.searchTerm,
        };

        return apiClient.getAllLogos(copy);
      },
      getNextPageParam: (lastPage) => {
        if (lastPage.pageIndex === lastPage.pageCount) {
          return undefined;
        }
        return lastPage.pageIndex + 1;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pageIndex === 1) {
          return undefined;
        }

        return firstPage.pageIndex - 1;
      },
    });
  }

  function deleteLogo(orgId: number, logoId: number): Promise<void> {
    // Optimistically remove from cache immediately
    queryClient.setQueriesData<PagedResponse<BaseLogo>>(
      ["logos"],
      (oldData) => {
        if (!oldData || !oldData.results) return oldData;

        return {
          ...oldData,
          results: oldData.results.filter((logo) => logo.id !== logoId),
        };
      },
    );

    // Remove individual logo cache entries. Predicate instead of a key
    // prefix: detail entries are per-act-as-scope (["logos", actAs, id]) and
    // nothing clears the cache on an identity switch, so the same deleted
    // logo can sit under several scopes — remove them ALL, not just the
    // current scope's.
    queryClient.removeQueries({
      predicate: (query) =>
        isLogoDetailQueryKey(query.queryKey) && query.queryKey[2] === logoId,
    });

    // Update infinite queries - use predicate function to match infinite queries
    queryClient.setQueriesData(["logos"], (oldData: any) => {
      if (!oldData || !oldData.pages) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map((page: PagedResponse<BaseLogo>) => ({
          ...page,
          results: page.results
            ? page.results.filter((logo: BaseLogo) => logo.id !== logoId)
            : [],
        })),
      };
    });

    return withClick(() =>
      apiClient.deleteLogoById(orgId, logoId).catch((error) => {
        // If deletion fails, invalidate queries to restore correct state
        invalidateQueries();
        throw error;
      })
    );
  }

  async function createImageLogo(
    logoFile: File,
    orgId: number,
  ): Promise<BaseLogo> {
    return withClick(async () => {
      let lr = await apiClient.createAndUploadImageLogo(logoFile, orgId);
      await invalidateQueries();
      return mapToLogo(lr);
    });
  }

  async function createTextLogo(
    value: string,
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createTextLogo(orgId, {
      color: color,
      heightMm: heightMM,
      value: value,
      font: font,
      customerId: customerId,
      method: method,
    });
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createNameLogo(
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
    customerId?: number,
    method?: string,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createPlaceholderNameLogo(orgId, {
      color: color,
      heightMm: heightMM,
      font: font,
      method: method,
      customerId: customerId,
    });
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createNumberLogo(
    color: string,
    heightMM: number,
    font: string,
    method: string,
    orgId: number,
    customerId?: number,
  ): Promise<BaseLogo> {
    let lr = await apiClient.createPlaceholderNumberLogo(orgId, {
      color: color,
      heightMm: heightMM,
      font: font,
      method: method,
      customerId: customerId,
    });
    await invalidateQueries();
    return mapToLogo(lr);
  }

  async function createBaseLogo(
    organizationId: number,
    title: string,
  ): Promise<BaseLogo> {
    // TODO Fix own logged in user's id.
    let lr = await apiClient.createImageLogo({
      title,
      customerId: organizationId,
    });
    return mapToLogo(lr);
  }

  async function uploadFileToBaseLogo(
    logo: File,
    customerId: number,
    logoId: number,
  ): Promise<BaseLogo> {
    return withClick(async () => {
      let lr = await apiClient.uploadFileImageLogo(logo, customerId, logoId);
      return mapToLogo(lr);
    });
  }

  // Flag REACT_APP_UPLOAD_SINGLE_REQUEST (§3C): createBaseLogo +
  // uploadFileToBaseLogo as ONE multipart POST organizations/{customerId}/
  // image-logo (file, title, customerId) - the same customer org in the
  // route as today's PUT and the same customerId as today's POST body, one
  // click id around the one call, and the answer mapped exactly like the
  // upload answer. Touches no cache: the actions handler seeds it as it
  // seeds the upload answer today. The 404-without-body fallback lives in
  // the actions handler (useLogoActionsHandler.handleCreateLogo).
  async function createAndUploadBaseLogo(
    logo: File,
    customerId: number,
    title: string,
  ): Promise<BaseLogo> {
    return withClick(async () => {
      const lr = await apiClient.createAndUploadImageLogo(
        logo,
        customerId,
        title,
        customerId,
      );
      return mapToLogo(lr);
    });
  }

  async function changeColors(
    orgId: Organization["id"],
    logoId: BaseLogo["id"],
    colors: ChangeLogoColors,
  ): Promise<BaseLogo> {
    return withClick(async () => {
    // 409 cloneUpload (flag cloneInstantEnabled): the clone's own supplier
    // identity is not there yet - info toast + ONE retry after the
    // envelope's delay; flag off = the plain call.
    const lrDto = await withCloneUploadRetry(() =>
      apiClient.changeColors(orgId, logoId, colors),
    );
    const lr = mapToLogo(lrDto as any);

    // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: a heal poll still in flight must
    // not land after this response and overwrite it (see logoDetailCache).
    cancelInFlightDetailFetch(queryClient, logoId);
    queryClient.setQueryData(["logos", actAsKey(), logoId], lr);
    queryClient.setQueriesData<PagedResponse<BaseLogo>>(
      ["logos"],
      (oldData) => {
        if (!oldData || !oldData.results) return oldData;
        return {
          ...oldData,
          results: oldData.results.map((item) =>
            item.id === logoId ? lr : item,
          ),
        };
      },
    );

    queryClient.setQueriesData(["logos"], (oldData: any) => {
      if (!oldData || !oldData.pages) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: PagedResponse<BaseLogo>) => ({
          ...page,
          results: page.results
            ? page.results.map((item: BaseLogo) =>
                item.id === logoId ? lr : item,
              )
            : [],
        })),
      };
    });

    return lr;
    });
  }

  async function changeTitle(
    orgId: Organization["id"],
    logoId: number,
    title: string,
  ): Promise<BaseLogo> {
    const lr = await apiClient.changeLogoTitle(orgId, logoId, title);
    await invalidateQueries();
    return lr;
  }

  async function update(orgId: number, logo: BaseLogo): Promise<BaseLogo> {
    // 409 cloneUpload (flag cloneInstantEnabled): only a METHOD change on a
    // preparing clone meets the guard (title/customer saves pass) - info
    // toast + ONE retry; flag off = the plain call.
    const lr = await withCloneUploadRetry(() =>
      apiClient.updateLogo(orgId, logo),
    );
    // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: the draft-save response never
    // reached the detail cache (raw DTO, no seed), so the editor's heal
    // subscription would have nothing to poll after a method change. Seed
    // the MAPPED response under the same key as every other mutation; the
    // return value stays the raw DTO callers already depend on.
    if (enviroment.logoHealPerMutationEnabled && lr && lr.id) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapped = mapToLogo(lr as any);
      cancelInFlightDetailFetch(queryClient, mapped.id);
      queryClient.setQueryData(["logos", actAsKey(), mapped.id], mapped);
    }
    return lr;
  }

  // Copy/resize used to AWAIT a full ["logos"] invalidation (refetching every
  // active AND inactive list query) before returning - twice per clone - which
  // held the click->editor path hostage for seconds. Instead: seed the detail
  // cache with the mapped response (the editor then renders with zero extra
  // GET, low-res preview included as a data: URI) and only mark list queries
  // stale; the list refetches when the user actually returns to it.
  function seedAndMarkStale(lrDto: BaseLogo): BaseLogo {
    const lr = mapToLogo(lrDto as any);
    // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: see changeColors.
    cancelInFlightDetailFetch(queryClient, lr.id);
    queryClient.setQueryData(["logos", actAsKey(), lr.id], lr);
    // Mark every logos query stale EXCEPT the detail entries (shared form
    // check — the just-seeded response must not be flagged stale before the
    // editor mounts on it).
    queryClient.invalidateQueries({
      queryKey: ["logos"],
      refetchType: "none",
      predicate: (query) => !isLogoDetailQueryKey(query.queryKey),
    });
    markLogoListAsStale();
    return lr;
  }

  async function resize(orgId: number, logo: BaseLogo): Promise<BaseLogo> {
    return withClick(async () => {
      // 409 cloneUpload (flag cloneInstantEnabled): see changeColors.
      const lr = await withCloneUploadRetry(() =>
        apiClient.resizeLogo(orgId, logo),
      );
      return seedAndMarkStale(lr);
    });
  }

  async function copy(orgId: number, logoId: number): Promise<BaseLogo> {
    // 409 cloneUpload (flag cloneInstantEnabled): cloning a clone that is
    // itself still preparing meets the guard on the SOURCE - info toast +
    // ONE retry; flag off = the plain call.
    const lr = await withCloneUploadRetry(() =>
      apiClient.copyLogo(orgId, logoId),
    );
    return seedAndMarkStale(lr);
  }

  async function commit(
    logo: BaseLogo,
    logoCommit: LogoCommit,
  ): Promise<BaseLogo> {
    return withClick(async () => {
      // 409 productPending (flag productPendingEnabled): info toast + ONE
      // retry after 3 s; flag off = the plain call. Same on the two commit
      // paths below - they are the endpoints behind the API's
      // ProductPendingGuard.
      const lr = await withProductPendingRetry(() =>
        apiClient.commitLogo(logo, logoCommit),
      );
      await invalidateQueries();
      return lr;
    });
  }

  async function placeVerdict(
    logo: BaseLogo,
    verdict: Verdict,
  ): Promise<BaseLogo> {
    return withClick(async () => {
    const lrDto = await withProductPendingRetry(() =>
      apiClient.placeVerdictLogo(logo, verdict),
    );
    const lr = mapToLogo(lrDto as any);
    queryClient.setQueryData(["logos", actAsKey(), lr.id], lr);
    queryClient.setQueriesData<PagedResponse<BaseLogo>>(
      ["logos"],
      (oldData) => {
        if (!oldData || !oldData.results) return oldData;
        return {
          ...oldData,
          results: oldData.results.map((item) =>
            item.id === lr.id ? lr : item,
          ),
        };
      },
    );
    queryClient.setQueriesData(["logos"], (oldData: any) => {
      if (!oldData || !oldData.pages) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: PagedResponse<BaseLogo>) => ({
          ...page,
          results: page.results
            ? page.results.map((item: BaseLogo) =>
                item.id === lr.id ? lr : item,
              )
            : [],
        })),
      };
    });
    return lr;
    });
  }

  async function initiateApproval(
    logo: BaseLogo,
    approval: InitiateApproval,
  ): Promise<BaseLogo> {
    return withClick(async () => {
    // Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED: the approval start scales
    // a draft above its method's max size down (maxSizeClamp in the answer).
    // With R5 that scale-down defers the product to the worker and the
    // guard may answer 409 productPending first; the ONE retry below then
    // meets a logo that already fits, and ITS answer carries no marker. The
    // 409 is remembered, and the marker is rebuilt when the retry's answer
    // is precisely the policy's result for the size that was sent (a
    // clone-upload 409 is not that case). No retry / flag off = as today.
    let productPendingRetry: unknown = null;
    const lrDto = await withProductPendingRetry(
      () => apiClient.initiateApprovalLogo(logo, approval),
      {
        onRetry: (error) => {
          productPendingRetry = error;
        },
      },
    );
    const lr = mapToLogo(lrDto as any);
    if (productPendingRetry !== null && !isCloneUploadPendingError(productPendingRetry)) {
      const inferred = inferApprovalClampAfterRetry(logo, lr);
      if (inferred) lr.maxSizeClamp = inferred;
    }

    queryClient.setQueryData(["logos", actAsKey(), lr.id], lr);
    await queryClient.invalidateQueries({
      queryKey: ["logos"],
      // Invalidate all 'logos' queries EXCEPT the detail entries (["logos",
      // actAs, id] — shared form check): the detail was just written with the
      // server response above.
      predicate: (query) => !isLogoDetailQueryKey(query.queryKey),
    });

    return lr;
    });
  }

  async function sendApproval(logo: BaseLogo, email: string): Promise<void> {
    const lr = await apiClient.sendApprovalLogo(logo, email);
  }

  async function sendApprovalConditional(
    logo: BaseLogo,
    email: string,
  ): Promise<void> {
    const lr = await apiClient.sendApprovalLogoConditional(logo, email);
  }

  async function generateTextLogoPreview(
    value: string,
    color: string,
    heightMM: number,
    font: string,
    orgId: number,
  ): Promise<string> {
    return await apiClient.generateTextLogoPreview(orgId, {
      color: color,
      heightMm: heightMM,
      value: value,
      font: font,
    });
  }

  async function generateNumberLogoPreview(
    color: string,
    heightMm: number,
    font: string,
    orgId: number,
  ): Promise<string> {
    return await apiClient.generateNumberLogoPreview(orgId, {
      color: color,
      heightMm: heightMm,
      font: font,
    });
  }

  async function refreshStorageCount(logoId: BaseLogo["id"]): Promise<void> {
    await apiClient.refreshStorageCount(logoId);
    await invalidateQueries();
  }

  async function getLogoStorageCounts(
    logoId: BaseLogo["id"],
  ): Promise<LogoStorageCountByOrgDto[]> {
    return await apiClient.getLogoStorageCounts(logoId);
  }

  async function refreshLogoStorageCountForOrg(
    logoId: BaseLogo["id"],
    organizationId: number,
  ): Promise<void> {
    await apiClient.refreshLogoStorageCountForOrg(logoId, organizationId);
    await invalidateQueries();
  }

  async function getImportLogosQueueCounts(): Promise<CreateMaskQueueCount[]> {
    return await apiClient.getImportLogosQueueCounts();
  }

  // React Query version for the header queue badges: dedups the header's repeated
  // remounts into one request (the imperative call used to fire ~5x per page load)
  // while keeping the 60s polling via refetchInterval.
  // Admin perf FE-6 (flag adminPerfQueueLazyEnabled): the HEADER instance
  // passes `open` (pointer over the queue box). With the flag it polls every
  // 5 min while not looked at, 60 s while it is, and refetches at once when
  // looked at after >55 s - so the number is never older than 60 s when the
  // admin looks at it, at ~80 % fewer apiPeter calls per tab. Callers that
  // pass no `open` (the import screens' own 60 s polls) are untouched, as
  // is everything with the flag off.
  function useImportLogosQueueCounts(options?: {
    open?: boolean;
  }): Resource<CreateMaskQueueCount[]> {
    const lazy =
      enviroment.adminPerfQueueLazyEnabled && options?.open !== undefined;
    const query = useQuery(
      ["import-queue-counts", "logos"],
      () => apiClient.getImportLogosQueueCounts(),
      lazy
        ? {
            refetchInterval: options?.open ? 60000 : 300000,
            staleTime: 55000,
            refetchOnMount: true,
          }
        : {
            refetchInterval: 60000,
            staleTime: 55000,
          },
    );
    const { isStale, refetch, isFetching } = query;
    useEffect(() => {
      if (lazy && options?.open && isStale && !isFetching) refetch();
      // Only the open/close edge matters (isStale/isFetching read at that moment).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazy, options?.open]);
    return query;
  }

  async function getImportPricesQueueCounts(): Promise<CreateMaskQueueCount[]> {
    return await apiClient.getImportPricesQueueCounts();
  }

  // Same shape as useImportLogosQueueCounts: react-query replaces the price-import
  // screen's raw 10-second setInterval poll — shared cache, 60s interval, paused
  // while the tab is unfocused (refetchIntervalInBackground defaults to false),
  // so an admin tab left open no longer drains the import host.
  function useImportPricesQueueCounts(): Resource<CreateMaskQueueCount[]> {
    return useQuery(
      ["import-queue-counts", "prices"],
      () => apiClient.getImportPricesQueueCounts(),
      {
        refetchInterval: 60000,
        staleTime: 55000,
      },
    );
  }

  async function getSupplierStatus(): Promise<SupplierStatus> {
    try {
      return mapSupplierStatus(await apiClient.getSupplierStatus());
    } catch (error) {
      if (!isSupplierStatusRouteMissing(error)) throw error;
      markSupplierStatusRouteMissing();
      return supplierStatusRouteMissing();
    }
  }

  return {
    useOptions,
    prefetchOptions,
    useTransferOptions,
    prefetchTransferOptions,
    useLogo,
    useAllLogos,
    useCustomerLogoLinks,
    prefetchCustomerLogoLinks,
    attachLogosToCustomer,
    detachLogoFromCustomer,
    prefetchLogos,
    useAllLogosForDesignInfinity,
    delete: deleteLogo,
    createLogo: createImageLogo,
    createTextLogo,
    createNameLogo,
    createNumberLogo,
    createBaseLogo,
    uploadFileToBaseLogo,
    createAndUploadBaseLogo,
    changeColors,
    changeTitle,
    update,
    resize,
    copy,
    commit,
    useAllLogosInfinity,
    initiateApproval,
    sendApproval,
    sendApprovalConditional,
    placeVerdict,
    generateTextLogoPreview,
    generateNumberLogoPreview,
    refreshStorageCount,
    getLogoStorageCounts,
    refreshLogoStorageCountForOrg,
    getImportLogosQueueCounts,
    useImportLogosQueueCounts,
    getImportPricesQueueCounts,
    useImportPricesQueueCounts,
    getSupplierStatus,
  };
}
